// "Verify your number": the customer proves the mobile they typed with a
// six-digit code by SMS (POST /auth/phone/codes → /auth/phone/verify). A
// verified number is what lets this device keep a profile, its orders and
// its loyalty card — and on a new phone, the same number brings them back
// (the server moves the device to the identity that already holds it).
// If the SMS cannot go out at all, the parent may let the customer carry on
// without it: food first.

import { useEffect, useState } from 'react';
import { ApiError, sendPhoneCode, verifyPhone, type PhoneVerified } from '../lib/api';

type Props = {
  /** the number as E.164, or null while it is not a real number yet */
  phone: string | null;
  /** this device's identity already holds the number */
  verified: boolean;
  onVerified: (v: PhoneVerified) => void;
  /** the code could not be sent (the SMS provider is down) */
  onUnavailable?: () => void;
};

export function VerifyPhone({ phone, verified, onVerified, onUnavailable }: Props) {
  const [stage, setStage] = useState<'idle' | 'sent' | 'unavailable'>('idle');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // a different number starts over
  useEffect(() => {
    setStage('idle');
    setCode('');
    setError(null);
  }, [phone]);

  if (!phone) return null;
  if (verified) {
    return <div className="vp-ok" data-verified={phone}>✓ Verified — your orders and rewards follow this number</div>;
  }

  async function send() {
    if (!phone || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendPhoneCode(phone);
      setStage('sent');
      setCode('');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'sms_failed') {
        setStage('unavailable');
        onUnavailable?.();
      } else {
        setError(e instanceof ApiError ? e.message : 'Could not send a code.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!phone || busy || code.trim().length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      onVerified(await verifyPhone(phone, code.trim()));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not check the code.');
      if (e instanceof ApiError && e.code !== 'wrong_code') setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vp" data-section="verify-phone" data-stage={stage}>
      <div className="vp-title">Verify your number</div>
      {stage === 'idle' && (
        <>
          <div className="co-hint">We'll text you a code. Verified, your orders and rewards stay with you — on this phone or the next.</div>
          <button className="co-apply vp-send" onClick={send} disabled={busy}>{busy ? 'Sending…' : 'Text me a code'}</button>
        </>
      )}
      {stage === 'sent' && (
        <>
          <div className="co-hint">Enter the 6-digit code we sent to {phone}.</div>
          <div className="co-row2">
            <input className="co-input" placeholder="6-digit code" aria-label="Verification code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => e.key === 'Enter' && verify()} />
            <button className="co-apply" onClick={verify} disabled={busy || code.length !== 6}>{busy ? '…' : 'Verify'}</button>
          </div>
          <button className="linky vp-resend" onClick={send} disabled={busy}>Send a new code</button>
        </>
      )}
      {stage === 'unavailable' && <div className="co-hint" data-unavailable>We couldn't text that number just now. You can carry on without verifying — verify next time to keep your history.</div>}
      {error && <div className="co-err" role="alert" data-verify-error>{error}</div>}
    </div>
  );
}
